import seoConfig from '~~/configs/seo.config';
import type { IEmbed, IImageMeta, IMetaData, IRawData, ISeoVideo } from '~~/types/local_types';
import type { ICmsContentEmbeds } from '~~/types/_cms-types';

const getModifiedOnString = (iso?: string): string | undefined => {
  let modifiedOn = undefined;
  if (iso) {
    const date = new Date(iso);

    modifiedOn =
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0') +
      '-' +
      String(date.getHours()).padStart(2, '0') +
      '-' +
      String(date.getMinutes()).padStart(2, '0');
  }

  return modifiedOn;
};
/**
 * Generates and applies SEO metadata and JSON-LD schema for a page.
 *
 * This composable handles:
 * - Metadata creation for SEO and social sharing (Open Graph, Twitter cards).
 * - canonical URL generation and application.
 * - JSON-LD schema generation (using `useSchemaOrg`).
 * - Dynamic handling for detail pages (e.g., articles) and overview pages (e.g., homepages).
 *
 * @param data - Input data for generating metadata.
 * @param data.pageTitle - The title of the page (fallback for overview pages).
 * @param data.pageDescription - A description of the page content (fallback for overview pages).
 * @param data.pageDescriptionBlocks - An array of text blocks for overview pages (fallback description).
 * @param data.pageImage - An image object for overview pages (fallback image metadata).
 * @param data.content - The content object for detail pages, containing specific metadata fields.
 * @param data.section - The section or category of the page (appended to titles).
 *
 * @returns The generated metadata object for optional additional use.
 */
export default function usePageSeo(data: IRawData): IMetaData {
  const { directusAssetsUrl } = useRuntimeConfig().public;
  const assetsEndpoint: string = `${(directusAssetsUrl as string).replace(/\/$/, '')}/`;

  // Generate the canonical URL
  const { origin, pathname }: URL = useRequestURL(); // Extract current origin and path
  const canonicalUrl = `${origin}${pathname}`.replace(/\/$/, ''); // Remove trailing slash for consistency

  // Add the canonical URL to the head of the document
  useHead({
    link: [{ rel: 'canonical', href: canonicalUrl }],
  });

  // Get the data from the input object
  const { pageTitle, pageDescription, pageDescriptionBlocks, pageImage, content, section } = data;

  const finalPageTitle = pageTitle || seoConfig.title;

  // Helper function to truncate description to a maximum length
  const truncateText = (text: string, maxLength: number = 160): string =>
    text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;

  // Helper function to construct Open Graph image metadata
  const getOgImageData = (image: IImageMeta): Partial<IMetaData> => ({
    ogImage: image.src,
    ogImageSecureUrl: image.src,
    ogImageWidth: image.width,
    ogImageHeight: image.height,
    ogImageAlt: image.alt,
    ogImageType: image.type || 'image/jpeg',
  });

  const getVideoData = (videoObjects?: Array<ICmsContentEmbeds>): ISeoVideo[] => {
    return (
      videoObjects
        ?.filter((videoObject) => videoObject !== undefined)
        .map((videoObject) => {
          const { embed, caption } = videoObject;
          const { oembed, url } = embed as IEmbed;

          return {
            url: oembed.src || url,
            embedUrl: oembed.src || url,
            description: caption || '',
            width: oembed.width || 960,
            height:
              typeof oembed.height === 'string' && oembed.height.endsWith('%')
                ? undefined
                : Number(oembed.height),
            thumbnailUrl: oembed.thumbnailUrl || undefined,
            provider: oembed.providerName,
          };
        }) || []
    );
  };

  let metaObject: IMetaData;

  // Metadata for detail pages
  if (content) {
    // Extract the mapped data from the original content object
    const {
      videos,
      publishTime,
      adjustTime,
      socialTitle = seoConfig.title,
      socialDescription = seoConfig.description,
      socialImage,
      socialVideo,
      tagsTitles,
      themesTitles,
      keywords,
      dossiers,
    } = useMapArticle(content);

    const modifiedOn = getModifiedOnString(socialImage?.modifiedOn);

    const ogImageData = socialImage
      ? getOgImageData({
          src: `${assetsEndpoint}${socialImage.id}.jpg?width=1200&height=630&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}`,
          width: 1200,
          height: 630,
          type: 'image/jpeg',
          alt: socialImage.description || socialTitle,
        })
      : getOgImageData({
          ...seoConfig.defaultImage,
          src: `${origin}${seoConfig.defaultImage.src}`,
        });

    metaObject = {
      ogType: 'article',
      ogTitle: socialTitle,
      description: truncateText(socialDescription),
      articlePublishedTime: publishTime || undefined,
      articleModifiedTime: adjustTime || publishTime || undefined,
      title: `${socialTitle}${section ? ` | ${section}` : ''}`,
      ...ogImageData,
      ...(tagsTitles?.length && { articleTag: tagsTitles }),
      ...(themesTitles?.length && { themesTitles }),
      ...(dossiers?.length && { dossiers }),
      ...(keywords?.length && { keywords }),
      ...(socialVideo && { socialVideoData: getVideoData([socialVideo])[0] }),
      ...(videos && { videoData: getVideoData([...videos]) }),
    };

    // If the article has a publish time, set the dc.date.issued meta tag for it
    if (metaObject.articlePublishedTime) {
      const date = new Date(metaObject.articlePublishedTime);
      const formattedDate = date.toISOString().split('T')[0]; // Format date as YYYY-MM-DD

      useHead({
        meta: [
          {
            name: 'dc.date.issued',
            content: formattedDate,
          },
        ],
      });
    }
  } else {

    const modifiedOn = getModifiedOnString(pageImage?.modifiedOn);

    // Metadata for overview pages
    const ogImageData = pageImage
      ? getOgImageData({
          src: `${assetsEndpoint}${pageImage.id}.jpg?width=1200&height=630&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}`,
          width: 1200,
          height: 630,
          type: 'image/jpeg',
          alt: pageImage.title || finalPageTitle,
        })
      : getOgImageData({
          ...seoConfig.defaultImage,
          src: `${origin}${seoConfig.defaultImage.src}`,
        });

    const computedPageDescription = computed(() => {
      if (pageDescriptionBlocks && pageDescriptionBlocks.length) {
        return sanitizeHTML(useMapTextBlocks(pageDescriptionBlocks) || '', {
          USE_PROFILES: { html: false },
        });
      }
      return pageDescription || seoConfig.description;
    });

    metaObject = {
      ogType: 'website',
      ogTitle: finalPageTitle,
      description: truncateText(computedPageDescription.value),
      title: `${finalPageTitle}${section ? ` | ${section}` : ''}`,
      ...ogImageData,
    };
  }

  // Apply SEO metadata
  useSeoMeta({
    title: metaObject.title,
    description: metaObject.description,
    robots: content?.articleType === 'shorts' ? 'noindex, nofollow' : seoConfig.robots,
    ogType: metaObject.ogType,
    ogTitle: metaObject.ogTitle,
    ogDescription: metaObject.description,
    twitterDescription: metaObject.description,
    twitterTitle: metaObject.ogTitle,
    twitterImage: metaObject.ogImage,
    ogImage: metaObject.ogImage,
    ogImageSecureUrl: metaObject.ogImageSecureUrl,
    ogImageWidth: metaObject.ogImageWidth,
    ogImageHeight: metaObject.ogImageHeight,
    ogImageAlt: metaObject.ogImageAlt,
    articlePublishedTime: metaObject.articlePublishedTime || false,
    articleModifiedTime: metaObject.articleModifiedTime || false,
    articleTag: metaObject.articleTag || false,
    ...(metaObject.socialVideoData?.provider &&
      ['youtube', 'avrotros'].includes(metaObject.socialVideoData.provider.toLowerCase()) && {
        ogVideo: metaObject.socialVideoData.embedUrl,
        ogVideoWidth: metaObject.socialVideoData.width,
        ogVideoHeight: metaObject.socialVideoData.height,
      }),
  });

  // Helper function for extracting the isPartOf schema property
  const getIsPartOf = () => {
    return (
      metaObject.dossiers &&
      metaObject.dossiers?.map(({ title, slug }) => ({
        '@type': 'CreativeWork',
        name: title,
        url: `${origin}/${slug}`,
      }))
    );
  };

  // Helper function for extracting the about schema property
  const getAbout = () => {
    return (
      metaObject.themesTitles &&
      metaObject.themesTitles?.map((title) => ({
        '@type': 'Thing',
        name: title,
      }))
    );
  };

  const getVideoSchema = (): object[] | undefined => {
    const allVideos = [metaObject.socialVideoData, ...(metaObject.videoData || [])];
    if (allVideos.length === 0) return undefined;

    return allVideos
      .filter((video) => video !== undefined)
      .map((video) => {
        const provider = video.provider?.toLowerCase() as string;

        return {
          '@type': ['youtube', 'avrotros', 'inos'].includes(provider)
            ? 'VideoObject'
            : 'SoftwareApplication',
          name: metaObject.ogTitle,
          description: truncateText(video.description || metaObject.description),
          embedUrl: video.embedUrl,
          contentUrl:
            provider === 'inos'
              ? video.embedUrl
              : video.url !== video.embedUrl
                ? video.url
                : undefined,
          uploadDate: metaObject.articlePublishedTime || undefined,
          width: video.width,
          height: video.height,
          thumbnailUrl: video.thumbnailUrl || metaObject.ogImage,
        };
      });
  };

  // Apply JSON-LD schema
  useSchemaOrg({
    '@context': 'https://schema.org',
    '@type': metaObject.ogType === 'article' ? 'NewsArticle' : 'WebPage',
    headline: metaObject.ogTitle,
    description: metaObject.description,
    datePublished: metaObject.articlePublishedTime || undefined,
    dateModified: metaObject.articleModifiedTime || undefined,
    author: metaObject.ogType === 'article' ? seoConfig.author : false,
    keywords: metaObject.keywords,
    articleSection: metaObject.themesTitles,
    isPartOf: getIsPartOf() || false,
    about: getAbout() || false,
    image: {
      '@type': 'ImageObject',
      url: metaObject.ogImage,
      width: metaObject.ogImageWidth,
      height: metaObject.ogImageHeight,
      caption: metaObject.ogImageAlt,
    },
    ...(metaObject.videoData &&
      metaObject.videoData?.length > 0 && {
        video: getVideoSchema(),
      }),
  });

  return metaObject;
}
